import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordSchema } from "@purlink/shared/auth";
import { Link, createFileRoute } from "@tanstack/react-router";
import { MailCheck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { AuthLayout, FormError } from "~/components/auth/auth-layout";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { authApi, errorMessage } from "~/lib/auth";

export const Route = createFileRoute("/_auth/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const form = useForm<z.input<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async ({ email }) => {
    try {
      const normalised = email.trim().toLowerCase();
      await authApi.requestPasswordReset(normalised);
      setSentTo(normalised);
    } catch (error) {
      form.setError("root.server", { message: errorMessage(error) });
    }
  });

  const back = (
    <Link to="/login" className="font-medium text-primary hover:underline">
      Back to sign in
    </Link>
  );

  if (sentTo) {
    return (
      <AuthLayout title="Check your email" footer={back}>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="glass-subtle grid size-12 place-items-center rounded-lg text-primary">
            <MailCheck className="size-5" />
          </div>
          <p className="text-body text-fg-secondary">
            If <strong className="text-fg">{sentTo}</strong> has a Purlink account, we've sent a
            link to reset the password. It expires in 1 hour.
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      description="Enter your email and we'll send you a link to choose a new password."
      footer={back}
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" autoComplete="username" autoFocus {...form.register("email")} />
        </Field>
        <FormError message={errors.root?.server?.message} />
        <Button type="submit" size="lg" loading={isSubmitting}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
